import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Search, RefreshCw } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Order } from "@/types/order-type";

type OrderStatus = "PAID" | "PENDING" | "SHIPPING" | "CANCELLED";

type RecentOrdersCardProps = {
  orders: Order[];
  loading?: boolean;
  defaultQuery?: string;
  defaultStatus?: "ALL" | OrderStatus;
  minHeight?: string;
  maxHeight?: string;
  onFilterChange?: (q: string, status: "ALL" | OrderStatus) => void;
  onRowClick?: (order: Order) => void;
  onRefresh?: () => void;
  currencyFormatter?: (n: number) => string;
};

const StatusBadge = ({ s }: { s: OrderStatus }) => {
  const styles: Record<OrderStatus, string> = {
    PAID: "bg-green-100 text-green-700",
    PENDING: "bg-yellow-100 text-yellow-700",
    SHIPPING: "bg-blue-100 text-blue-700",
    CANCELLED: "bg-red-100 text-red-700",
  };
  return <Badge className={`rounded-full text-[10px] md:text-xs px-2 h-5 md:h-6 ${styles[s]}`}>{s}</Badge>;
};

const defaultCurrency = (n: number, cur = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: cur }).format(n);

export const RecentOrdersCard: React.FC<RecentOrdersCardProps> = ({
  orders,
  loading = false,
  defaultQuery = "",
  defaultStatus = "ALL",
  minHeight = "260px",
  maxHeight = "420px",
  onFilterChange,
  onRowClick,
  onRefresh,
  currencyFormatter = defaultCurrency,
}) => {
  const [q, setQ] = useState(defaultQuery);
  const [status, setStatus] = useState<"ALL" | OrderStatus>(defaultStatus);

  useEffect(() => {
    onFilterChange?.(q, status);
  }, [q, status, onFilterChange]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return orders.filter((o) => {
      const matchesStatus = status === "ALL" || o.status === status;
      const matchesQuery =
        !ql ||
        o.id.toLowerCase().includes(ql) ||
        o.customerName?.toLowerCase().includes(ql) ||
        o.source?.toLowerCase().includes(ql);
      return matchesStatus && matchesQuery;
    });
  }, [orders, q, status]);

  return (
    <Card className="xl:col-span-7 h-full flex flex-col">
      <CardHeader className="pb-4 border-b">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold">Recent Orders</CardTitle>
            {onRefresh && (
              <Button variant="ghost" size="icon" onClick={onRefresh} disabled={loading}>
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            )}
          </div>

          <div className="flex flex-row items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 h-9 md:w-full"
              />
            </div>
            <select
              className="h-9 text-sm border rounded-md px-2 bg-background w-[110px] md:w-auto"
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
            >
              <option value="ALL">All Status</option>
              <option value="PAID">PAID</option>
              <option value="PENDING">PENDING</option>
              <option value="SHIPPING">SHIPPING</option>
              <option value="CANCELLED">CANCELLED</option>
            </select>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div
          className="overflow-auto"
          style={{ minHeight, maxHeight }}
        >
          <Table>
            <TableHeader className="bg-muted/50 sticky top-0 z-10">
              <TableRow>
                <TableHead className="w-[80px] text-[11px] md:text-xs">Order#</TableHead>
                <TableHead className="text-[11px] md:text-xs">Customer</TableHead>
                {/* Desktop columns */}
                <TableHead className="hidden md:table-cell text-right">Items</TableHead>
                <TableHead className="hidden md:table-cell text-right">Total</TableHead>
                {/* Mobile combined column */}
                <TableHead className="md:hidden text-right">Order Summary</TableHead>
                <TableHead className="text-center w-[100px]">Status</TableHead>
                <TableHead className="hidden sm:table-cell text-right">Date</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {!loading && filtered.map((o) => (
                <TableRow
                  key={o.id}
                  className="hover:bg-muted/30 cursor-pointer transition-colors"
                  onClick={() => onRowClick?.(o)}
                >
                  <TableCell className="py-3">
                    <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                      #{o.orderNumber}
                    </span>
                  </TableCell>

                  <TableCell>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium line-clamp-1">{o.customerName}</span>
                      <span className="sm:hidden text-[10px] text-muted-foreground">
                        {o.createdAt && format(new Date(o.createdAt), "MMM d, HH:mm")}
                      </span>
                    </div>
                  </TableCell>

                  {/* Desktop Only */}
                  <TableCell className="hidden md:table-cell text-right text-sm">
                    {o.totalItems}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-right font-semibold text-sm">
                    {currencyFormatter(o.totalAmount ?? 0)}
                  </TableCell>

                  {/* Mobile Only: Merged View */}
                  <TableCell className="md:hidden text-right py-3">
                    <div className="flex flex-col items-end">
                      <span className="text-sm font-bold">{currencyFormatter(o.totalAmount ?? 0)}</span>
                      <span className="text-[10px] text-muted-foreground">{o.totalItems} items</span>
                    </div>
                  </TableCell>

                  <TableCell className="text-center">
                    <StatusBadge s={o.status as OrderStatus} />
                  </TableCell>

                  {/* Desktop Only */}
                  <TableCell className="hidden sm:table-cell text-right text-[11px] text-muted-foreground">
                    {o.createdAt ? format(new Date(o.createdAt), "yyyy-MM-dd HH:mm") : "--"}
                  </TableCell>
                </TableRow>
              ))}

              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    No matching orders found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};